// ═══════════════════════════════════════════════════════════════════════════════
// DRUGS.JS — EMERGENCY / IV / PARENTERAL DRUG REFERENCE
// Oral/syrup formulations have been moved to oralFormulations.js
// This file retains: resuscitation, RSI, IV antibiotics, IV sedation,
//                    anticonvulsants (IV/PR), fluids, electrolytes, infusions
// References: Harriet Lane 22nd Ed · IAP 2024 · Piyush Gupta 18th Ed · PALS 2020
// ═══════════════════════════════════════════════════════════════════════════════

export const DRUGS = [

  // ══════════════════════════════════════════════════════════════════
  // RESUSCITATION
  // ══════════════════════════════════════════════════════════════════
  {
    id: "adrenaline-arrest",
    name: "Adrenaline (Epinephrine)",
    indication: "Cardiac arrest",
    category: "resuscitation",
    dosePerKg: 0.01,
    unit: "mg",
    max: 1,
    route: "IV/IO",
    notes: "Repeat every 3–5 min. 1:10,000 concentration (0.1 mL/kg).",
  },
  {
    id: "adrenaline-vol",
    name: "Adrenaline vol (1:10,000)",
    indication: "Cardiac arrest",
    category: "resuscitation",
    dosePerKg: 0.1,
    unit: "mL",
    max: 10,
    route: "IV/IO",
    notes: "0.1 mL/kg of 1:10,000 solution.",
  },
  {
    id: "adrenaline-im",
    name: "Adrenaline IM",
    indication: "Anaphylaxis",
    category: "resuscitation",
    dosePerKg: 0.01,
    unit: "mg",
    max: 0.5,
    route: "IM (1:1000)",
    notes: "Anterolateral thigh. Repeat every 5–15 min.",
  },
  {
    id: "atropine",
    name: "Atropine",
    indication: "Bradycardia / pre-intubation",
    category: "resuscitation",
    dosePerKg: 0.02,
    unit: "mg",
    min: 0.1,
    max: 1,             // ← CORRECTED: was 0.5 mg — this caps adolescents incorrectly. Harriet Lane: max 0.5 mg child, 1 mg adolescent. Set max to 1 mg (adolescent ceiling) and rely on clinical judgement for younger children.
    route: "IV/IO",
    notes: "Min 0.1 mg. Max 0.5 mg (child), 1 mg (adolescent). Repeat q5 min PRN. Pre-intubation: same dose. OP poisoning: NO minimum dose — see atropine-op entry.",
  },
  {
    id: "adenosine",
    name: "Adenosine (1st dose)",
    indication: "SVT",
    category: "resuscitation",
    dosePerKg: 0.1,
    unit: "mg",
    max: 6,
    route: "Rapid IV push",
    notes: "Follow with 5 mL saline flush. 2nd dose: 0.2 mg/kg (max 12 mg).",
  },
  {
    id: "amiodarone",
    name: "Amiodarone",
    indication: "VF/VT arrest, SVT",
    category: "resuscitation",
    dosePerKg: 5,
    unit: "mg",
    max: 300,
    route: "IV/IO",
    notes: "Bolus for refractory VF/pulseless VT.",
  },
  {
    id: "naloxone",
    name: "Naloxone",
    indication: "Opioid overdose",
    category: "resuscitation",
    dosePerKg: 0.1,
    unit: "mg",
    max: 2,
    route: "IV / IM / IN",
    notes: "Titrate to respiratory response.",
  },
  {
    id: "flumazenil",
    name: "Flumazenil",
    indication: "Benzodiazepine reversal",
    category: "toxicology",
    dosePerKg: 0.01,
    unit: "mg",
    max: 0.2,
    route: "IV",
    notes: "Caution: may precipitate seizures.",
  },
  {
    id: "diphenhydramine",
    name: "Diphenhydramine",
    indication: "Anaphylaxis adjunct / allergic reaction",
    category: "resuscitation",
    dosePerKg: 1,
    unit: "mg",
    max: 50,
    route: "IV / IM",
    notes: "Adjunct only — never replaces adrenaline in anaphylaxis.",
  },
  {
  id: "chlorpheniramine-iv",
  name: "Chlorpheniramine (Avil) IV/IM",
  indication: "Allergic reaction / anaphylaxis adjunct / urticaria / drug reactions",
  category: "resuscitation",
  dosePerKg: 0.1,
  unit: "mg",
  max: 10,
  route: "IV slow / IM",
  notes: "0.1 mg/kg IV slow over 1 min (max 10 mg) or IM. Avil 22.75 mg/mL ampoule (2 mL = 45.5 mg). Common Indian dose: 2.5 mg (<1 yr), 5 mg (1–5 yr), 10 mg (>5 yr). Sedating — monitor for drowsiness and urinary retention. ADJUNCT only — never replaces adrenaline in anaphylaxis.",
},
  {
    id: "hydrocortisone",
    name: "Hydrocortisone",
    indication: "Septic shock / adrenal crisis / severe asthma",
    category: "resuscitation",
    dosePerKg: 2,
    unit: "mg",
    max: 100,
    route: "IV",
    notes: "Shock 2 mg/kg; anaphylaxis 4 mg/kg; adrenal crisis 50–100 mg stat.",
  },
  {
    id: "methylprednisolone",
    name: "Methylprednisolone",
    indication: "Severe asthma / allergy / spinal cord injury",
    category: "endocrine",
    dosePerKg: 2,
    unit: "mg",
    max: 60,
    route: "IV",
    notes: "1–2 mg/kg/dose IV. Spinal cord: 30 mg/kg load then 5.4 mg/kg/hr × 23 hr.",
  },
  {
    id: "glucagon",
    name: "Glucagon",
    indication: "Hypoglycaemia / BB or CCB OD",
    category: "resuscitation",
    fixedDose: "0.5 mg (<20 kg) · 1 mg (≥20 kg) IM",
    unit: "mg",
    route: "IM / IV / SC",
    notes: "BB overdose: 50 mcg/kg IV bolus then infusion.",
  },
  {
    id: "naltrexone",
    name: "Naltrexone",
    indication: "Opioid reversal (long-acting)",
    category: "resuscitation",
    fixedDose: "Not first-line for acute OD — use naloxone",
    unit: "—",
    route: "PO / IM",
    notes: "Used for opioid use disorder. For acute overdose: naloxone preferred.",
  },

  // ══════════════════════════════════════════════════════════════════
  // ELECTROLYTES
  // ══════════════════════════════════════════════════════════════════
  {
    id: "calcium-gluconate",
    name: "Calcium gluconate 10%",
    indication: "Hypocalcaemia / hyperkalaemia / CCB OD",
    category: "resuscitation",
    dosePerKg: 0.5,
    unit: "mL",
    max: 20,
    route: "IV slow (10%)",
    notes: "Harriet Lane: 100 mg/kg (=1 mL/kg 10%) for hyperK, 50 mg/kg maintenance.",
  },
  {
    id: "calcium-chloride",
    name: "Calcium chloride 10%",
    indication: "Cardiac arrest (CCB/HF)",
    category: "resuscitation",
    dosePerKg: 0.2,
    unit: "mL",
    max: 10,
    route: "IV central (10%)",
    notes: "20 mg/kg (0.2 mL/kg 10%). 3× more elemental Ca than gluconate.",
  },
  {
    id: "magnesium-sulfate",
    name: "Magnesium sulfate",
    indication: "Severe asthma / torsades / eclampsia",
    category: "resuscitation",
    dosePerKg: 25,      // ← CORRECTED: was 50 mg/kg — the HIGH end of range. Harriet Lane: 25–50 mg/kg (max 2 g). Use 25 mg/kg as default starting dose; severe asthma may use up to 50 mg/kg.
    unit: "mg",
    max: 2000,
    route: "IV over 20 min",
    notes: "25–50 mg/kg IV over 20 min (max 2 g; Harriet Lane). Severe asthma: 50 mg/kg over 20 min. Torsades/hypoMg: 25–50 mg/kg. Monitor for hypotension, respiratory depression, loss of patellar reflexes.",
  },
  {
    id: "sodium-bicarb",
    name: "Sodium bicarbonate 8.4%",
    indication: "Severe metabolic acidosis / hyperK / TCA OD",
    category: "resuscitation",
    dosePerKg: 1,
    unit: "mEq",
    max: 50,
    route: "IV slow",
    notes: "1 mEq/kg. Not routine in arrest. Dilute 1:1 with sterile water in neonate.",
  },
  {
    id: "lidocaine-antiarrhythmic",
    name: "Lidocaine (antiarrhythmic)",
    indication: "VT / VF (alternative to amiodarone)",
    category: "resuscitation",
    dosePerKg: 1,
    unit: "mg",
    max: 100,
    route: "IV/IO",
    notes: "1 mg/kg bolus. Infusion 20–50 mcg/kg/min.",
  },

  // ══════════════════════════════════════════════════════════════════
  // VASOACTIVE / INOTROPES (INFUSIONS)
  // ══════════════════════════════════════════════════════════════════
  {
    id: "dopamine",
    name: "Dopamine infusion",
    indication: "Shock / inotropy",
    category: "resuscitation",
    fixedDose: "2–20 mcg/kg/min",
    unit: "mcg/kg/min",
    route: "IV infusion",
    notes: "Renal 2–5, β 5–10, α >10. Titrate. Central line preferred.",
  },
  {
    id: "dobutamine",
    name: "Dobutamine infusion",
    indication: "Cardiogenic shock",
    category: "resuscitation",
    fixedDose: "2–20 mcg/kg/min",
    unit: "mcg/kg/min",
    route: "IV infusion",
    notes: "β1 inotrope. Titrate to perfusion.",
  },
  {
    id: "noradrenaline",
    name: "Noradrenaline infusion",
    indication: "Warm septic shock",
    category: "resuscitation",
    fixedDose: "0.05–1 mcg/kg/min",
    unit: "mcg/kg/min",
    route: "IV infusion (central)",
    notes: "α1 vasopressor. First-line in warm shock.",
  },
  {
    id: "adrenaline-infusion",
    name: "Adrenaline infusion",
    indication: "Cold septic shock / post-arrest",
    category: "resuscitation",
    fixedDose: "0.05–1 mcg/kg/min",
    unit: "mcg/kg/min",
    route: "IV infusion",
    notes: "First-line in cold shock.",
  },
  {
    id: "milrinone",
    name: "Milrinone",
    indication: "Low-CO / post cardiac surgery",
    category: "resuscitation",
    fixedDose: "Load 50 mcg/kg, then 0.25–0.75 mcg/kg/min",
    unit: "mcg/kg/min",
    route: "IV infusion",
    notes: "Phosphodiesterase inhibitor. Risk of hypotension.",
  },
  {
    id: "vasopressin",
    name: "Vasopressin infusion",
    indication: "Catecholamine-resistant shock",
    category: "resuscitation",
    fixedDose: "0.0003–0.002 units/kg/min",
    unit: "units/kg/min",
    route: "IV infusion",
    notes: "V1 vasoconstrictor. Second-line vasopressor.",
  },

  // ══════════════════════════════════════════════════════════════════
  // ANTIHYPERTENSIVES (IV/URGENT)
  // References: Harriet Lane 22nd Ed · PALS 2020 · AHA Paediatric HTN 2017
  // ══════════════════════════════════════════════════════════════════
  {
    id: "labetalol-iv",
    name: "Labetalol (IV)",
    indication: "Hypertensive emergency / urgency",
    category: "antihypertensive",
    dosePerKg: 0.2,
    unit: "mg",
    max: 40,
    route: "IV bolus or infusion",
    notes: "Bolus: 0.2–1 mg/kg IV over 2 min (max 40 mg/dose). Infusion: 0.25–3 mg/kg/hr. α+β blocker. Avoid in asthma, heart block, cardiogenic shock. Harriet Lane: preferred IV agent for hypertensive emergency in children.",
  },
  {
    id: "hydralazine-iv",
    name: "Hydralazine (IV/IM)",
    indication: "Hypertensive emergency / eclampsia",
    category: "antihypertensive",
    dosePerKg: 0.2,
    unit: "mg",
    max: 20,
    route: "IV / IM",
    notes: "0.1–0.5 mg/kg IV/IM q4–6h (max 20 mg/dose). Direct arterial vasodilator. Reflex tachycardia common — combine with beta-blocker. Onset 10–20 min IV. Indian brands: Nepresol (Novartis).",
  },
  {
    id: "nifedipine-sl",
    name: "Nifedipine (sublingual/oral)",
    indication: "Hypertensive urgency (not emergency)",
    category: "antihypertensive",
    dosePerKg: 0.25,
    unit: "mg",
    max: 10,
    route: "SL / PO (liquid from capsule)",
    notes: "0.25–0.5 mg/kg PO/SL (max 10 mg). Bite and swallow capsule or aspirate liquid with syringe. NOT for hypertensive emergency — precipitous fall. Onset 20–30 min. Harriet Lane: use cautiously, monitor closely.",
  },
  {
    id: "amlodipine-iv",
    name: "Amlodipine (PO — IV not available)",
    indication: "Chronic hypertension / hypertensive urgency",
    category: "antihypertensive",
    dosePerKg: 0.1,
    unit: "mg",
    max: 5,
    route: "PO (no IV formulation)",
    notes: "0.1–0.3 mg/kg/day PO OD (max 5 mg/day, up to 10 mg in adolescents). No IV formulation exists. Long-acting CCB — not for acute emergency. Indian brands: Amlip (Cipla), Stamlo (Dr. Reddy's), Amlopin (Sun).",
  },
  {
    id: "enalaprilat-iv",
    name: "Enalaprilat (IV)",
    indication: "Hypertensive emergency (non-neonatal) / heart failure",
    category: "antihypertensive",
    dosePerKg: 0.005,
    unit: "mg",
    max: 1.25,
    route: "IV over 5 min",
    notes: "0.005–0.01 mg/kg IV q8–24h (max 1.25 mg/dose). IV form of enalapril (ACE inhibitor). Contraindicated in bilateral renal artery stenosis, pregnancy, hyperkalemia. Avoid in neonates (renal failure risk). Onset 15–30 min IV. Indian brands: Envas (Cadila), Enam IV.",
  },
  {
    id: "sodium-nitroprusside",
    name: "Sodium nitroprusside",
    indication: "Severe hypertensive emergency / afterload reduction",
    category: "antihypertensive",
    fixedDose: "0.3–8 mcg/kg/min IV infusion",
    unit: "mcg/kg/min",
    route: "IV infusion (light-protected)",
    notes: "Start 0.3 mcg/kg/min, titrate to 8 mcg/kg/min max. Protect from light (wrap tubing/bag in foil). Cyanide toxicity risk >3 mcg/kg/min or >72 hr — monitor thiocyanate levels. Requires arterial line + continuous BP monitoring. Harriet Lane: most potent IV antihypertensive.",
  },
  {
    id: "phentolamine-iv",
    name: "Phentolamine",
    indication: "Phaeochromocytoma crisis / catecholamine excess / extravasation",
    category: "antihypertensive",
    dosePerKg: 0.1,
    unit: "mg",
    max: 5,
    route: "IV bolus / SC infiltration",
    notes: "Hypertensive crisis: 0.05–0.1 mg/kg IV (max 5 mg) — repeat q5 min PRN. Extravasation: 5–10 mg in 10 mL NS infiltrated into affected area within 12 hr. α-blocker. Reflex tachycardia — have propranolol ready.",
  },
  {
    id: "captopril-po",
    name: "Captopril (PO)",
    indication: "Hypertension / heart failure / neonatal hypertension",
    category: "antihypertensive",
    dosePerKg: 0.1,
    unit: "mg",
    max: 6,
    route: "PO (crushed/solution)",
    notes: "Start 0.1 mg/kg/dose TDS (max 6 mg/kg/day). Neonates: 0.01 mg/kg/dose TDS (extreme caution). ACE inhibitor — check K⁺ and creatinine. Onset 15–30 min. Available as 12.5 mg, 25 mg tabs (Capoten, Aceten).",
  },
  {
    id: "clonidine-iv",
    name: "Clonidine (IV/PO)",
    indication: "Hypertensive urgency / opioid/alcohol withdrawal / ADHD adjunct",
    category: "antihypertensive",
    dosePerKg: 0.002,
    unit: "mg",
    max: 0.3,
    route: "IV slow / PO",
    notes: "IV: 2–6 mcg/kg over 10–15 min. PO: 5–10 mcg/kg/day ÷ BD-TDS. α2-agonist — rebound hypertension if stopped abruptly. Sedation, bradycardia. Monitor HR. Indian brands: Catapres (Boehringer), Arkamin (Torrent).",
  },

  // ══════════════════════════════════════════════════════════════════
  // ANTIARRHYTHMICS (beyond those already listed)
  // ══════════════════════════════════════════════════════════════════
  {
    id: "procainamide-iv",
    name: "Procainamide (IV)",
    indication: "SVT (adenosine-refractory) / VT with pulse / WPW",
    category: "antiarrhythmic",
    dosePerKg: 15,
    unit: "mg",
    max: 1000,
    route: "IV over 30–60 min",
    notes: "Load: 15 mg/kg IV over 30–60 min (max 1 g). Maintenance infusion: 20–80 mcg/kg/min. Stop if QRS widens >50%, hypotension, or arrhythmia converts. Class Ia antiarrhythmic. Harriet Lane: preferred agent for WPW with SVT when adenosine fails. Not widely available India — amiodarone often substituted.",
  },
  {
    id: "verapamil-iv",
    name: "Verapamil (IV)",
    indication: "SVT (adenosine-refractory, age >1 yr ONLY)",
    category: "antiarrhythmic",
    dosePerKg: 0.1,
    unit: "mg",
    max: 5,
    route: "IV over 2 min",
    notes: "0.1–0.3 mg/kg IV over 2 min (max 5 mg/dose). May repeat 0.1–0.2 mg/kg in 30 min. CONTRAINDICATED <1 yr (fatal hypotension), WPW, heart failure, beta-blocker concurrent use. Have calcium gluconate ready for reversal. Harriet Lane: use only if adenosine fails and >1 yr.",
  },
  {
    id: "sotalol-iv",
    name: "Sotalol (IV/PO)",
    indication: "Ventricular arrhythmias / SVT prophylaxis",
    category: "antiarrhythmic",
    dosePerKg: 1,
    unit: "mg",
    max: 80,
    route: "IV over 5 min / PO",
    notes: "IV: 1 mg/kg over 5 min (max 80 mg). PO: 2–8 mg/kg/day ÷ BD-TDS. β-blocker + Class III. Monitor QT (risk of torsades). Reduce dose in renal impairment. Indian brands: Sotacor (BMS), Sotalol (Sun).",
  },
  {
    id: "digoxin-load",
    name: "Digoxin (IV loading)",
    indication: "SVT / atrial flutter / heart failure with AF",
    category: "antiarrhythmic",
    dosePerKg: 20,
    unit: "mcg",
    max: 1000,
    route: "IV (digitalising dose)",
    notes: "Total digitalising dose (TDD): Neonate: 20–30 mcg/kg IV; Child 1 mo–2 yr: 30–40 mcg/kg; Child >2 yr: 20–30 mcg/kg (max 1 mg). Give ½ TDD stat, then ¼ TDD q8–12h × 2 doses. Maintenance: 5–10 mcg/kg/day ÷ BD. Monitor ECG, electrolytes. Target level 0.8–2 ng/mL. Indian brands: Lanoxin (GSK), Digoxin (Sun).",
  },

  // ══════════════════════════════════════════════════════════════════
  // PULMONARY HYPERTENSION
  // ══════════════════════════════════════════════════════════════════
  {
    id: "sildenafil-iv",
    name: "Sildenafil (PO/IV)",
    indication: "Pulmonary arterial hypertension / persistent pulmonary HT of newborn (PPHN)",
    category: "pulmonary-ht",
    dosePerKg: 0.5,
    unit: "mg",
    max: 20,
    route: "PO / IV",
    notes: "PO: 0.5–1 mg/kg/dose TDS-QDS (max 20 mg TDS in children). IV: 0.1 mg/kg/dose TDS (max 10 mg). PDE-5 inhibitor — increases pulmonary vasodilation. Avoid if systemic hypotension. Neonatal PPHN: 0.5–1 mg/kg q6–12h PO. Indian brands: Revatio (Pfizer 20 mg), Suhagra (Cipla — adult brand used off-label).",
  },
  {
    id: "bosentan-po",
    name: "Bosentan (PO)",
    indication: "Pulmonary arterial hypertension (WHO class II–IV)",
    category: "pulmonary-ht",
    dosePerKg: 2,
    unit: "mg",
    max: 62.5,
    route: "PO BD",
    notes: "Start 2 mg/kg BD for 4 weeks, then 4 mg/kg BD (max 62.5 mg BD if <20 kg; 125 mg BD if ≥20 kg). Endothelin receptor antagonist. Monitor LFTs monthly (hepatotoxic). Teratogenic. Indian brands: Tracleer (Actelion), Bosentas (Cipla). Expensive — Pradhan Mantri Jan Aushadhi Scheme.",
  },
  {
    id: "nitric-oxide",
    name: "Inhaled Nitric Oxide (iNO)",
    indication: "PPHN / hypoxic respiratory failure / post-cardiac surgery pulmonary HT",
    category: "pulmonary-ht",
    fixedDose: "Start 20 ppm inhaled, titrate 5–40 ppm",
    unit: "ppm inhaled",
    route: "Via ventilator circuit",
    notes: "Selective pulmonary vasodilator — does not affect systemic BP. Start 20 ppm, may increase to 40 ppm. Wean slowly to avoid rebound. Monitor methaemoglobin (<2.5%). Requires NO delivery system (INOmax). Wean by 5 ppm q4–6h once FiO₂ improving. Harriet Lane: first-line for term newborn PPHN with hypoxic respiratory failure.",
  },
  {
    id: "iloprost-inh",
    name: "Iloprost (inhaled)",
    indication: "Pulmonary arterial hypertension / iNO weaning",
    category: "pulmonary-ht",
    fixedDose: "2.5–5 mcg/dose inhaled 6–9×/day",
    unit: "mcg inhaled",
    route: "Via Prodose nebuliser",
    notes: "Prostacyclin analogue. Requires specific nebuliser device (Prodose AAD). 2.5 mcg per dose, titrate to 5 mcg. 6–9 doses/day. Duration each session 4–10 min. Flushing, hypotension. Indian brands: Ventavis (Bayer) — high cost.",
  },

  // ══════════════════════════════════════════════════════════════════
  // ENDOCRINE
  // ══════════════════════════════════════════════════════════════════
  {
    id: "octreotide",
    name: "Octreotide",
    indication: "Hyperinsulinaemic hypoglycaemia / GI bleeding / chylothorax",
    category: "endocrine",
    dosePerKg: 1,
    unit: "mcg",
    max: 50,
    route: "SC / IV infusion",
    notes: "Hypoglycaemia: 1–5 mcg/kg/dose SC q6–8h or 1–10 mcg/kg/hr IV infusion. GI bleed: 1–2 mcg/kg IV bolus then 1–2 mcg/kg/hr. Chylothorax: 0.5–4 mcg/kg/hr IV. Somatostatin analogue. Monitor glucose — rebound hypoglycaemia on stopping. Indian brands: Sandostatin (Novartis).",
  },
  {
    id: "desmopressin-iv",
    name: "Desmopressin (DDAVP)",
    indication: "Diabetes insipidus / bleeding (vWD, haemophilia A) / enuresis",
    category: "endocrine",
    dosePerKg: 0.3,
    unit: "mcg",
    max: 20,
    route: "IV / IN / SC / PO",
    notes: "DI: 0.05–0.3 mcg/kg/dose IV/SC q12–24h (max 20 mcg). Bleeding: 0.3 mcg/kg IV over 30 min (max 20 mcg) — repeat q12–24h × 2–3 doses (tachyphylaxis). IN: 5–20 mcg per nostril. Monitor Na⁺ (hyponatraemia risk — restrict fluids after dose). Indian brands: Minirin (Ferring), Nocdurna.",
  },
  {
    id: "hydrocortisone-stress",
    name: "Hydrocortisone (stress dose)",
    indication: "Adrenal insufficiency stress dosing / congenital adrenal hyperplasia",
    category: "endocrine",
    dosePerKg: 50,
    unit: "mg",
    max: 100,
    route: "IV / IM",
    notes: "Stress/crisis dose: 50–100 mg/m² IV/IM stat (or 1–2 mg/kg). CAH sick day rules: triple oral dose; if vomiting give IM hydrocortisone 25 mg (<5 yr), 50 mg (5–12 yr), 100 mg (≥12 yr). Maintenance: 10–15 mg/m²/day ÷ TDS PO. Indian brands: Solu-Cortef (Pfizer), Efcorlin (Pfizer).",
  },
  {
    id: "fludrocortisone",
    name: "Fludrocortisone",
    indication: "Mineralocorticoid replacement — CAH / primary adrenal insufficiency",
    category: "endocrine",
    fixedDose: "0.05–0.2 mg/day PO OD",
    unit: "mg/day PO",
    route: "PO OD",
    notes: "Neonates: 0.1–0.3 mg/day. Children: 0.05–0.2 mg/day OD. Monitor BP, electrolytes (Na⁺, K⁺), plasma renin. No IV formulation — hydrocortisone has sufficient mineralocorticoid activity in crisis. Indian brands: Florinef (Squibb — often imported), Fludrocort.",
  },
  {
    id: "propylthiouracil",
    name: "Propylthiouracil (PTU)",
    indication: "Thyroid storm / severe hyperthyroidism (preferred in 1st trimester, thyroid storm)",
    category: "endocrine",
    dosePerKg: 5,
    unit: "mg",
    max: 200,
    route: "PO / NG (no IV form)",
    notes: "Thyroid storm load: 5–7 mg/kg/dose PO q8h (adult 200–400 mg q4h). Maintenance: 5 mg/kg/day ÷ TDS. Preferred over methimazole in thyroid storm (blocks T4→T3 conversion peripherally). Agranulocytosis risk — check WBC. Indian brands: PTU (generic), Propycil.",
  },
  {
    id: "methimazole",
    name: "Methimazole (Carbimazole)",
    indication: "Hyperthyroidism / Graves' disease (maintenance)",
    category: "endocrine",
    dosePerKg: 0.5,
    unit: "mg",
    max: 20,
    route: "PO TDS",
    notes: "0.5–1 mg/kg/day ÷ TDS (max 30 mg/day). Carbimazole 10 mg ≈ methimazole 6 mg (prodrug conversion). Monitor TFTs q4–6 weeks. Agranulocytosis (0.1–0.5%) — stop if fever/sore throat. Indian brands: Neomercazole (Sanofi — carbimazole 5 mg), Thyrozol.",
  },
  {
    id: "propranolol-thyroid",
    name: "Propranolol (thyroid storm / SVT)",
    indication: "Thyroid storm symptom control / SVT / portal hypertension",
    category: "endocrine",
    dosePerKg: 0.01,
    unit: "mg",
    max: 3,
    route: "IV slow / PO",
    notes: "Thyroid storm IV: 0.01 mg/kg IV slowly over 10 min (max 3 mg). PO: 0.5–2 mg/kg/day ÷ TDS. Blocks adrenergic symptoms (tachycardia, tremor, sweating). Give 1 hr after PTU (not before — prevents thyroid hormone release). Contraindicated in asthma, cardiogenic shock. Indian brands: Inderal (AstraZeneca), Propranolol (Sun).",
  },

  // ══════════════════════════════════════════════════════════════════
  // TOXICOLOGY / ANTIDOTES
  // ══════════════════════════════════════════════════════════════════
  {
    id: "nac-iv",
    name: "N-Acetylcysteine (NAC)",
    indication: "Paracetamol overdose / acute liver failure",
    category: "toxicology",
    fixedDose: "Load 150 mg/kg in 200 mL D5W over 60 min → 50 mg/kg over 4 hr → 100 mg/kg over 16 hr",
    unit: "mg/kg IV (3-phase)",
    route: "IV infusion (Rumack protocol)",
    notes: "Phase 1: 150 mg/kg in 200 mL D5W over 60 min (max 6 g in neonates). Phase 2: 50 mg/kg over 4 hr. Phase 3: 100 mg/kg over 16 hr. Total dose 300 mg/kg over 21 hr. Anaphylactoid reactions (5–10%) — slow infusion, treat with antihistamine. Most effective <8 hr post-ingestion. Indian brands: Acemuc IV (Sun), Parvolex (Alliance — imported).",
  },
  {
    id: "atropine-op",
    name: "Atropine (organophosphate poisoning)",
    indication: "Organophosphate / carbamate poisoning",
    category: "toxicology",
    dosePerKg: 0.05,
    unit: "mg",
    max: 4,
    route: "IV / IM / IO",
    notes: "Initial: 0.05 mg/kg IV (no minimum dose unlike cardiac bradycardia). Double dose every 5–10 min until secretions dry (not heart rate target). Severe poisoning may require 10–100× normal doses. Endpoint: drying of secretions, not tachycardia. Infusion: 10–20% of atropinising dose/hr. Harriet Lane: no minimum dose restriction in OP poisoning.",
  },
  {
    id: "pralidoxime",
    name: "Pralidoxime (2-PAM)",
    indication: "Organophosphate poisoning (cholinesterase reactivation)",
    category: "toxicology",
    dosePerKg: 25,
    unit: "mg",
    max: 1000,
    route: "IV over 15–30 min",
    notes: "Load: 25–50 mg/kg IV over 15–30 min (max 1–2 g/dose). Infusion: 10–20 mg/kg/hr (max 500 mg/hr). Give within 24–36 hr of exposure (before ageing irreversible). Use with atropine — does NOT replace atropine. Indian brands: P-2-AM (Neon), Pralidoxime chloride (Troikaa). WHO Essential Medicine for OP poisoning.",
  },
  {
    id: "physostigmine",
    name: "Physostigmine",
    indication: "Anticholinergic toxidrome (severe — atropine/antihistamine/TCA OD)",
    category: "toxicology",
    dosePerKg: 0.02,
    unit: "mg",
    max: 2,
    route: "IV slow (over 5 min)",
    notes: "0.02 mg/kg IV over 5 min (max 0.5 mg in children; 2 mg in adults). May repeat in 5–10 min if no response. Reversible cholinesterase inhibitor — crosses BBB. Contraindicated in TCA OD (may cause seizures/asystole — use only for pure anticholinergic). Atropine available as antidote. Limited India availability.",
  },
  {
    id: "digoxin-fab",
    name: "Digoxin-specific Fab (Digibind/DigiFab)",
    indication: "Severe digoxin toxicity / life-threatening arrhythmia",
    category: "toxicology",
    fixedDose: "Dose based on ingested amount or serum level — see notes",
    unit: "vials IV",
    route: "IV over 30 min",
    notes: "Known ingestion: vials = (mg ingested × 0.8) ÷ 0.5. Serum level: vials = (level [ng/mL] × weight [kg]) ÷ 100. Empiric: 10–20 vials adult, 1–2 vials (38 mg/vial) child. Each vial (38 mg Digibind) binds 0.5 mg digoxin. Monitor K⁺ (hypokalaemia post-treatment). Very expensive, limited India availability — consult toxicology.",
  },
  {
    id: "intralipid-20",
    name: "Intralipid 20% (Lipid Emulsion)",
    indication: "Local anaesthetic systemic toxicity (LAST) / lipophilic drug OD",
    category: "toxicology",
    dosePerKg: 1.5,
    unit: "mL",
    max: 100,
    route: "IV bolus then infusion",
    notes: "LAST protocol: 1.5 mL/kg IV bolus over 1 min (max 100 mL). Then 0.25–0.5 mL/kg/min for 10 min. May repeat bolus × 2 if no response. Max cumulative 10 mL/kg. Use for bupivacaine/ropivacaine/lipophilic drug cardiac arrest. Indian brands: Intralipid (Fresenius Kabi), Lipovenoes (Braun).",
  },

  // ══════════════════════════════════════════════════════════════════
  // HAEMATOLOGY
  // ══════════════════════════════════════════════════════════════════
  {
    id: "tranexamic-acid",
    name: "Tranexamic Acid (TXA)",
    indication: "Trauma haemorrhage / surgical bleeding / haemophilia / heavy menstrual bleeding",
    category: "haematology",
    dosePerKg: 15,
    unit: "mg",
    max: 1000,
    route: "IV over 10 min / PO",
    notes: "Trauma: 15 mg/kg IV over 10 min (max 1 g), then 2 mg/kg/hr × 8 hr (max 1 g). Give within 3 hr of injury. Surgical: 10–20 mg/kg IV. Antifibrinolytic. Monitor for seizures at high doses (neuronal glycine antagonism). Indian brands: Pause (Lupin), Tranexa (Meyer), Transamin (Daiichi).",
  },
  {
    id: "phytomenadione-iv",
    name: "Phytomenadione (Vitamin K1) IV",
    indication: "Vitamin K deficiency bleeding / warfarin reversal / liver disease coagulopathy",
    category: "haematology",
    dosePerKg: 0.3,
    unit: "mg",
    max: 10,
    route: "IV slow (over 20–30 min) / IM",
    notes: "VKDB / warfarin reversal: 0.3 mg/kg IV over 20–30 min (max 10 mg). NEVER rapid IV bolus (anaphylaxis risk). IM preferred when IV not needed urgently. Neonatal prophylaxis: 1 mg IM at birth. Effect in 4–6 hr IV, 12–24 hr PO. Indian brands: Konakion MM (Roche), Phytomenadione (Cipla).",
  },
  {
    id: "ffp",
    name: "Fresh Frozen Plasma (FFP)",
    indication: "Coagulopathy / DIC / warfarin reversal / liver disease / TTP",
    category: "haematology",
    dosePerKg: 15,
    unit: "mL",
    max: 600,
    route: "IV over 30–60 min",
    notes: "10–20 mL/kg IV over 30–60 min (max ~600 mL for larger children). Each mL/kg raises factor levels ~1–2%. Contains all clotting factors, protein C, protein S. ABO compatible preferred. Thaw time 20–30 min — plan ahead. Monitor for TACO/TRALI. Harriet Lane: give before invasive procedures if PT/aPTT >1.5× normal.",
  },
  {
    id: "platelets",
    name: "Platelet Transfusion",
    indication: "Thrombocytopaenia with bleeding / pre-procedure / ITP with severe bleed",
    category: "haematology",
    dosePerKg: 10,
    unit: "mL",
    max: 300,
    route: "IV over 20–30 min",
    notes: "5–10 mL/kg (1 unit per 10 kg) raises platelets by 50,000–100,000/µL. Threshold for prophylactic transfusion: <10,000 stable, <20,000 with fever/mucositis, <50,000 pre-procedure, <100,000 neurosurgery. Give ABO-compatible. Irradiated platelets for immunocompromised.",
  },
  {
    id: "cryoprecipitate",
    name: "Cryoprecipitate",
    indication: "Fibrinogen deficiency / haemophilia A / vWD / DIC with hypofibrinogenaemia",
    category: "haematology",
    dosePerKg: 2,
    unit: "mL",
    max: 200,
    route: "IV over 10–15 min",
    notes: "1–2 mL/kg (1 unit per 5 kg). Each unit (~15–20 mL) contains fibrinogen 150–250 mg, FVIII 80 units, vWF, FXIII. Target fibrinogen >100 mg/dL. Use when fibrinogen <100 mg/dL with active bleeding or pre-surgery. ABO compatibility preferred. Thaw at 37°C and use within 4–6 hr.",
  },
  {
    id: "protamine",
    name: "Protamine sulfate",
    indication: "Heparin reversal / post-cardiac surgery / LMWH overdose",
    category: "haematology",
    fixedDose: "1 mg protamine per 100 units heparin given in past 2 hr",
    unit: "mg IV",
    route: "IV over 10 min",
    notes: "UFH reversal: 1 mg protamine per 100 units UFH (given last 2 hr, max 50 mg). LMWH partial reversal: 1 mg per 1 mg enoxaparin. Max rate 5 mg/min. Risk: anaphylaxis, hypotension, bradycardia — have adrenaline ready. Fish allergy: use caution. Indian brands: Protamine Sulphate (IDPL, Neon).",
  },
  {
    id: "desmopressin-bleeding",
    name: "Desmopressin (bleeding — vWD/haemophilia A)",
    indication: "Mild haemophilia A / type 1 vWD / platelet dysfunction",
    category: "haematology",
    dosePerKg: 0.3,
    unit: "mcg",
    max: 20,
    route: "IV over 30 min / IN",
    notes: "0.3 mcg/kg IV over 30 min (max 20 mcg). Releases stored vWF and FVIII from endothelium. Repeat q12–24h × 2–3 doses (tachyphylaxis). Restrict fluids 1 hr before and 8 hr after (hyponatraemia risk). Contraindicated in type 2B vWD (thrombocytopaenia worsens). Indian brands: Minirin (Ferring).",
  },
  {
    id: "albumin-5",
    name: "Albumin 5%",
    indication: "Hypovolaemia / septic shock / hypoalbuminaemia / spontaneous bacterial peritonitis",
    category: "haematology",
    dosePerKg: 10,
    unit: "mL",
    max: 500,
    route: "IV over 30–60 min",
    notes: "10–20 mL/kg IV (1–2 g/kg) for volume expansion. SBP: 1.5 g/kg on day 1, 1 g/kg on day 3. Caution: volume overload in cardiac/renal disease. Preferred over synthetic colloids in liver disease, sepsis with hypoalbuminaemia. Indian brands: Albucrit (Reliance Life Sciences), Alburex (CSL Behring).",
  },
  {
    id: "albumin-20",
    name: "Albumin 20%",
    indication: "Oedematous states / nephrotic syndrome / severe hypoalbuminaemia",
    category: "haematology",
    dosePerKg: 1,
    unit: "g",
    max: 25,
    route: "IV over 2–4 hr",
    notes: "0.5–1 g/kg (2.5–5 mL/kg of 20%). Give with furosemide in nephrotic syndrome to aid diuresis. Target albumin >2.5 g/dL. Slower infusion than 5% — monitor for pulmonary oedema. Indian brands: Albucrit 20% (Reliance), Buminate 20% (Baxter).",
  },
  {
    id: "ivig",
    name: "IVIG (IV Immunoglobulin)",
    indication: "Kawasaki disease / ITP / GBS / immunodeficiency / neonatal alloimmune thrombocytopaenia",
    category: "haematology",
    dosePerKg: 2,
    unit: "g",
    max: 100,
    route: "IV over 10–12 hr",
    notes: "Kawasaki: 2 g/kg IV over 10–12 hr (single dose). ITP: 0.8–1 g/kg × 1–2 doses. GBS: 0.4 g/kg/day × 5 days. Immunodeficiency: 400–600 mg/kg q3–4 weeks. Premedicate (paracetamol ± diphenhydramine). Aseptic meningitis risk. Monitor for haemolysis. Indian brands: Biovac-I (Wockhardt), Sandoglobulin (Novartis), Intravenous IG (Dr. Reddy's).",
  },

  // ══════════════════════════════════════════════════════════════════
  // RENAL / HYPERKALAEMIA
  // ══════════════════════════════════════════════════════════════════
  {
    id: "calcium-resonium",
    name: "Calcium resonium (Kayexalate)",
    indication: "Hyperkalaemia (non-emergency / chronic)",
    category: "renal",
    dosePerKg: 1,
    unit: "g",
    max: 15,
    route: "PO / PR",
    notes: "1 g/kg/dose PO or PR (max 15 g). PO: mix with sorbitol or honey. PR: retain for 60 min. Onset hours — NOT for emergency hyperK (use calcium gluconate, insulin-dextrose, salbutamol). Constipation. Not in neonates (ileal necrosis risk). Indian brands: Resonium (Sanofi), Kayexalate.",
  },
  {
    id: "sodium-zirconium",
    name: "Sodium Zirconium Cyclosilicate (SZC)",
    indication: "Hyperkalaemia (non-dialysis management)",
    category: "renal",
    fixedDose: "10 g PO TDS × 48 hr (adult dose — limited paediatric data)",
    unit: "g PO",
    route: "PO (suspension)",
    notes: "Adult: 10 g TDS × 48 hr (emergency phase) then 5–10 g OD (maintenance). Paediatric data limited. Potassium-selective cation exchanger. Faster onset than resonium (~1 hr). Safe in post-operative ileus. Indian brands: Lokelma (AstraZeneca) — very expensive, limited availability.",
  },
  {
    id: "insulin-dextrose-hyperk",
    name: "Insulin + Dextrose (hyperkalaemia)",
    indication: "Acute hyperkalaemia (transcellular shift)",
    category: "renal",
    fixedDose: "0.1 units/kg actrapid + 0.5 g/kg dextrose (= 2 mL/kg D25 or 5 mL/kg D10)",
    unit: "units/kg + g/kg",
    route: "IV over 30 min",
    notes: "Onset 20–30 min, lasts 2–4 hr. Monitor glucose q30 min × 2 hr (hypoglycaemia risk). Reduces K⁺ by 0.5–1.5 mEq/L. Combine with other measures (calcium gluconate for cardiac protection, salbutamol, resonium for elimination). Harriet Lane standard hyperK protocol.",
  },

  // ══════════════════════════════════════════════════════════════════
  // SEDATION / RSI (IV)
  // ══════════════════════════════════════════════════════════════════
  {
    id: "ketamine-sedation",
    name: "Ketamine (procedural sedation)",
    indication: "Procedural sedation",
    category: "sedation",
    dosePerKg: 1.5,
    unit: "mg",
    route: "IV",
    notes: "1–2 mg/kg IV or 4–5 mg/kg IM. Preserves airway reflexes. Onset 1 min IV / 5 min IM.",
  },
  {
    id: "ketamine-rsi",
    name: "Ketamine (RSI)",
    indication: "Rapid sequence intubation",
    category: "sedation",
    dosePerKg: 2,
    unit: "mg",
    route: "IV",
    notes: "Preferred in shock/asthma. Bronchodilation. Haemodynamically stable.",
  },
  {
    id: "propofol",
    name: "Propofol",
    indication: "RSI induction / procedural sedation",
    category: "sedation",
    dosePerKg: 2,
    unit: "mg",
    route: "IV",
    notes: "Hypotension risk. Avoid in shock. No analgesia — combine with opioid.",
  },
  {
    id: "etomidate",
    name: "Etomidate (RSI)",
    indication: "RSI induction — haemodynamically unstable",
    category: "sedation",
    dosePerKg: 0.3,
    unit: "mg",
    route: "IV",
    notes: "Haemodynamically neutral. Avoid in septic shock (adrenal suppression). Onset 30 s.",
  },
  {
    id: "midazolam-iv",
    name: "Midazolam (IV)",
    indication: "IV sedation / status epilepticus / ICU sedation",
    category: "sedation",
    dosePerKg: 0.1,
    unit: "mg",
    max: 5,
    route: "IV",
    notes: "0.05–0.1 mg/kg IV. Respiratory depression — monitor SpO₂. Reversible with flumazenil.",
  },
  {
    id: "midazolam-in",
    name: "Midazolam (IN/buccal)",
    indication: "Seizure / sedation without IV access",
    category: "anticonvulsant",
    dosePerKg: 0.2,
    unit: "mg",
    max: 10,
    route: "IN / Buccal",
    notes: "Intranasal or buccal route. Onset 5–10 min.",
  },
  {
    id: "dexmedetomidine",
    name: "Dexmedetomidine",
    indication: "ICU sedation / procedural sedation / weaning agitation",
    category: "sedation",
    fixedDose: "Load 0.5–1 mcg/kg over 10 min, then 0.2–0.7 mcg/kg/hr",
    unit: "mcg/kg/hr",
    route: "IV infusion",
    notes: "α2-agonist. Sedation without respiratory depression. Bradycardia and hypotension — monitor. No analgesia. Useful for MRI/procedures and weaning from mechanical ventilation.",
  },

  // ══════════════════════════════════════════════════════════════════
  // NEUROMUSCULAR BLOCKING AGENTS
  // ══════════════════════════════════════════════════════════════════
  {
    id: "suxamethonium",
    name: "Suxamethonium (Succinylcholine)",
    indication: "RSI paralysis",
    category: "airway",
    dosePerKg: 2,
    unit: "mg",
    route: "IV",
    notes: "Onset 30–60 s, duration 8–10 min. Depolarising. Contraindicated in hyperkalemia, burns >24h, crush injury, denervation. Malignant hyperthermia trigger.",
  },
  {
    id: "rocuronium",
    name: "Rocuronium",
    indication: "RSI paralysis / intubation",
    category: "airway",
    dosePerKg: 1.2,
    unit: "mg",
    route: "IV",
    notes: "Onset 60 s. Duration 45–60 min. Non-depolarising. Reversible with sugammadex 16 mg/kg.",
  },
  {
    id: "vecuronium",
    name: "Vecuronium",
    indication: "Paralysis (non-RSI) / ICU paralysis",
    category: "airway",
    dosePerKg: 0.1,
    unit: "mg",
    route: "IV",
    notes: "0.1 mg/kg intubation dose. Onset 2–3 min. Duration 25–40 min. Infusion: 0.05–0.1 mg/kg/hr. No cardiovascular effects.",
  },
  {
    id: "cisatracurium",
    name: "Cisatracurium",
    indication: "ICU paralysis (renal/hepatic failure preferred)",
    category: "airway",
    dosePerKg: 0.15,
    unit: "mg",
    route: "IV",
    notes: "0.15 mg/kg bolus. Infusion 1–3 mcg/kg/min. Hofmann elimination — safe in renal/hepatic failure. No histamine release.",
  },

  // ══════════════════════════════════════════════════════════════════
  // ANTICONVULSANTS (IV/PR — acute management)
  // ══════════════════════════════════════════════════════════════════
  {
    id: "lorazepam",
    name: "Lorazepam (IV)",
    indication: "Status epilepticus — first-line IV benzodiazepine",
    category: "anticonvulsant",
    dosePerKg: 0.1,
    unit: "mg",
    max: 4,
    route: "IV",
    notes: "0.05–0.1 mg/kg IV (max 4 mg). First-line IV benzodiazepine for SE. Onset 2–5 min, duration 12–24 hr. Slower to inject than diazepam — dilute 1:1 with NS.",
  },
  {
    id: "diazepam-iv",
    name: "Diazepam (IV/PR)",
    indication: "Status epilepticus / febrile convulsion",
    category: "anticonvulsant",
    dosePerKg: 0.2,
    unit: "mg",
    max: 10,
    route: "IV / PR",
    notes: "0.2–0.3 mg/kg IV or 0.5 mg/kg PR. PR: use IV solution rectally if suppository unavailable. Inject IV slowly (<2 mg/min). Duration shorter than lorazepam.",
  },
  {
    id: "levetiracetam-iv",
    name: "Levetiracetam (IV load)",
    indication: "Status epilepticus — 2nd line after benzodiazepines",
    category: "anticonvulsant",
    dosePerKg: 40,
    unit: "mg",
    max: 3000,
    route: "IV over 5–15 min",
    notes: "40–60 mg/kg IV (max 3 g). After benzodiazepines fail. Dilute in NS, infuse over 5–15 min. Minimal haemodynamic effects. Same drug as oral — switch to PO maintenance when possible.",
  },
  {
    id: "phenytoin",
    name: "Phenytoin / Fosphenytoin",
    indication: "Status epilepticus — 2nd line",
    category: "anticonvulsant",
    dosePerKg: 20,
    unit: "mg PE",
    max: 1500,
    route: "IV slow (ECG monitoring)",
    notes: "20 mg PE/kg IV (max 1500 mg PE). Max rate: phenytoin 1 mg/kg/min, fosphenytoin 3 mg PE/kg/min. Monitor ECG (bradycardia, QT prolongation). Extravasation risk (phenytoin) — central line preferred.",
  },
  {
    id: "phenobarbital-iv",
    name: "Phenobarbital (IV load)",
    indication: "Status epilepticus (3rd line) / neonatal seizures",
    category: "anticonvulsant",
    dosePerKg: 20,
    unit: "mg",
    max: 600,
    route: "IV slow (1 mg/kg/min)",
    notes: "Load 20 mg/kg IV (max 600 mg). Neonates: 20 mg/kg, may repeat 10 mg/kg × 2 doses. Max rate 1 mg/kg/min. Respiratory depression — have BVM ready.",
  },
  {
    id: "valproate-iv",
    name: "Sodium Valproate (IV)",
    indication: "Status epilepticus — 2nd/3rd line",
    category: "anticonvulsant",
    dosePerKg: 30,
    unit: "mg",
    max: 3000,
    route: "IV over 5–10 min",
    notes: "30–40 mg/kg IV (max 3 g). Good option when phenytoin contraindicated. Avoid in liver disease, pregnancy (teratogenic). Monitor LFTs. Indian brands: Valparin IV.",
  },
  {
    id: "thiopentone-iv",
    name: "Thiopentone (Refractory SE)",
    indication: "Refractory status epilepticus — requires intubation",
    category: "anticonvulsant",
    dosePerKg: 3,
    unit: "mg",
    max: 500,
    route: "IV slow (requires intubation)",
    notes: "3–5 mg/kg IV bolus, then 1–5 mg/kg/hr infusion. Requires intubation. EEG monitoring for burst suppression. Indian brands: Pentothal.",
  },

  // ══════════════════════════════════════════════════════════════════
  // ANALGESIA (IV/IM/IN — emergency use)
  // ══════════════════════════════════════════════════════════════════
  {
    id: "morphine-iv",
    name: "Morphine (IV/IM)",
    indication: "Severe pain / post-operative / palliative",
    category: "analgesia",
    dosePerKg: 0.1,
    unit: "mg",
    max: 5,
    route: "IV / IM",
    notes: "0.05–0.1 mg/kg IV q2–4h. Titrate to pain. Respiratory depression — have naloxone ready. Infusion: 10–30 mcg/kg/hr.",
  },
  {
    id: "fentanyl-iv",
    name: "Fentanyl (IV)",
    indication: "Procedural / severe pain / RSI analgesia",
    category: "analgesia",
    dosePerKg: 1,
    unit: "mcg",
    max: 50,
    route: "IV slow",
    notes: "1–2 mcg/kg IV slow (over 3–5 min). Risk of chest-wall rigidity with fast push. Infusion: 1–3 mcg/kg/hr.",
  },
  {
    id: "fentanyl-in",
    name: "Fentanyl (IN)",
    indication: "Acute analgesia without IV access",
    category: "analgesia",
    dosePerKg: 1.5,
    unit: "mcg",
    max: 100,
    route: "Intranasal",
    notes: "1.5 mcg/kg IN. Use atomiser — split between nostrils if >1 mL. Onset 5–10 min. Rapid onset without IV required.",
  },
  {
    id: "ketamine-analgesia",
    name: "Ketamine (sub-dissociative analgesia)",
    indication: "Acute pain / opioid-sparing",
    category: "analgesia",
    dosePerKg: 0.3,
    unit: "mg",
    max: 30,
    route: "IV slow (over 10 min)",
    notes: "0.2–0.5 mg/kg IV over 10 min. Lower emergence reactions than sedation doses. IN route: 0.5–1 mg/kg (max 50 mg) for pre-IV trauma analgesia.",
  },
  {
    id: "ketamine-in",
    name: "Ketamine (IN analgesia)",
    indication: "Acute pain without IV",
    category: "analgesia",
    dosePerKg: 1,
    unit: "mg",
    max: 50,
    route: "Intranasal",
    notes: "0.5–1 mg/kg IN. Useful for trauma analgesia pre-IV access. Onset 5–10 min.",
  },
  {
    id: "ketorolac-iv",
    name: "Ketorolac (IV/IM)",
    indication: "Moderate pain / renal colic / post-op",
    category: "analgesia",
    dosePerKg: 0.5,
    unit: "mg",
    max: 30,
    route: "IV / IM",
    notes: "0.5 mg/kg IV q6h. Max 30 mg/dose, 120 mg/day. Avoid >5 days, renal impairment, active bleeding.",
  },
  {
    id: "paracetamol-iv",
    name: "Paracetamol (IV — Perfalgan)",
    indication: "Pain / fever when oral route unavailable",
    category: "analgesia",
    dosePerKg: 15,
    unit: "mg",
    max: 1000,
    route: "IV over 15 min",
    notes: "15 mg/kg q6h IV (max 1 g/dose, 60 mg/kg/day). Indian brands: Perfalgan 100 mL (10 mg/mL). Switch to oral as soon as possible. Give over 15 min.",
  },
  {
    id: "lidocaine-local",
    name: "Lidocaine (local, plain)",
    indication: "Local anaesthesia",
    category: "analgesia",
    dosePerKg: 4.5,
    unit: "mg",
    max: 300,
    route: "SC / infiltration",
    notes: "Max 4.5 mg/kg plain. Max 7 mg/kg with epinephrine. 1% = 10 mg/mL, 2% = 20 mg/mL.",
  },
  {
    id: "bupivacaine-local",
    name: "Bupivacaine (local)",
    indication: "Local / regional anaesthesia / nerve blocks",
    category: "analgesia",
    dosePerKg: 2,
    unit: "mg",
    max: 175,
    route: "SC / nerve block",
    notes: "Max 2 mg/kg (2.5 mg/kg with epi). 0.25% = 2.5 mg/mL. Cardiotoxic in overdose — intralipid rescue.",
  },
  {
    id: "sucrose-24",
    name: "Sucrose 24% (Sweet-Ease)",
    indication: "Procedural analgesia (neonate ≤ 3 mo)",
    category: "analgesia",
    fixedDose: "0.5–2 mL onto tongue 2 min pre-procedure",
    unit: "mL PO",
    route: "Oral drops",
    notes: "For minor procedures (venepuncture, heel-prick, IM) in neonates/young infants. Combine with non-nutritive sucking.",
  },
  {
    id: "nitrous-oxide",
    name: "Nitrous oxide / O₂ (Entonox 50:50)",
    indication: "Procedural analgesia/sedation (≥5 yr cooperative)",
    category: "analgesia",
    fixedDose: "50% N₂O / 50% O₂ — self-administered inhalation",
    unit: "inhaled",
    route: "Inhaled (self-admin)",
    notes: "Onset 30–60 s, offset ~5 min. Avoid in pneumothorax, bowel obstruction, head injury, B12 deficiency, <4 yr.",
  },

  // ══════════════════════════════════════════════════════════════════
  // GI / HEPATIC
  // ══════════════════════════════════════════════════════════════════
  {
    id: "omeprazole-iv",
    name: "Omeprazole (IV)",
    indication: "GI bleeding / peptic ulcer / GERD (when oral unavailable)",
    category: "gi-hepatic",
    dosePerKg: 1,
    unit: "mg",
    max: 40,
    route: "IV over 20–30 min",
    notes: "0.5–1 mg/kg/day IV OD-BD (max 40 mg/dose). GI bleed: 0.5–1 mg/kg bolus then 0.1–0.2 mg/kg/hr infusion. Reconstitute with NS. Switch to oral PPI as soon as tolerated. Indian brands: Omez IV (Dr. Reddy's), Pantop IV (Aristo — pantoprazole preferred IV).",
  },
  {
    id: "pantoprazole-iv",
    name: "Pantoprazole (IV)",
    indication: "GI bleeding / peptic ulcer disease / stress ulcer prophylaxis",
    category: "gi-hepatic",
    dosePerKg: 1,
    unit: "mg",
    max: 40,
    route: "IV over 15 min",
    notes: "0.5–1 mg/kg/dose OD-BD IV (max 40 mg/dose). High-dose for GI bleed: 1 mg/kg bolus then 0.1–0.2 mg/kg/hr for 72 hr. More stable for IV use than omeprazole. Indian brands: Pantop IV (Aristo), Pan-IV (Alkem), Pantocid IV (Sun).",
  },
  {
    id: "ranitidine-iv",
    name: "Ranitidine (IV) — Note: recall issues",
    indication: "Stress ulcer prophylaxis / GERD / GI bleed adjunct",
    category: "gi-hepatic",
    dosePerKg: 1,
    unit: "mg",
    max: 50,
    route: "IV over 30 min",
    notes: "1–2 mg/kg IV q6–8h (max 50 mg/dose). NDMA contamination recalls in many countries — PPIs preferred. Still available some Indian centres. H2 blocker. Indian brands: Rantac IV (JB Chemicals), Zinetac IV (GSK).",
  },
    {
    id: "ondansetron-iv",
    name: "Ondansetron (IV)",
    indication: "Vomiting / post-op nausea",
    category: "gi-hepatic",
    dosePerKg: 0.15,
    unit: "mg",
    max: 8,
    route: "IV over 15 min",
    notes: "Max 4 mg if <15 kg; 8 mg if ≥15 kg. Avoid in long QT. Dilute, give over 15 min.",
  },
  {
    id: "metoclopramide-iv",
    name: "Metoclopramide (IV)",
    indication: "Vomiting / gastric dysmotility / GERD / migraine-associated vomiting",
    category: "gi-hepatic",
    dosePerKg: 0.1,
    unit: "mg",
    max: 10,
    route: "IV slow (over 5 min)",
    notes: "0.1 mg/kg IV over 5 min (max 10 mg, or 0.5 mg/kg/day max). Extrapyramidal reactions (dystonias) — give diphenhydramine 1 mg/kg IV as rescue. Not recommended <1 yr (methaemoglobinaemia). Indian brands: Perinorm IV (Abbott), Reglan.",
  },
  {
    id: "lactulose",
    name: "Lactulose",
    indication: "Hepatic encephalopathy / constipation",
    category: "gi-hepatic",
    dosePerKg: 1,
    unit: "mL",
    max: 30,
    route: "PO / NG / PR",
    notes: "Hepatic encephalopathy: 0.5 mL/kg TDS (titrate to 2–3 soft stools/day). Constipation: 1–2 mL/kg OD-BD. PR: 300 mL lactulose in 700 mL water as retention enema q4–6h. Indian brands: Duphalac (Solvay/Abbott), Lactomove, Looz.",
  },
  {
    id: "neomycin-gi",
    name: "Neomycin (GI decontamination)",
    indication: "Hepatic encephalopathy (GI luminal) / bowel preparation",
    category: "gi-hepatic",
    dosePerKg: 12.5,
    unit: "mg",
    max: 500,
    route: "PO / NG (not absorbed)",
    notes: "50 mg/kg/day ÷ QDS (max 2 g/day). Non-absorbable aminoglycoside — reduces ammonia-producing gut bacteria. Use with lactulose for hepatic encephalopathy. Rifaximin preferred in adults (limited paediatric data). Indian brands: Neomycin (Sun, Cipla).",
  },
  {
    id: "terlipressin",
    name: "Terlipressin",
    indication: "Variceal bleeding / hepatorenal syndrome",
    category: "gi-hepatic",
    fixedDose: "0.01–0.02 mg/kg IV bolus q4–6h (max 2 mg/dose)",
    unit: "mg/dose IV",
    route: "IV bolus q4h",
    notes: "Child: 0.01–0.02 mg/kg q4–6h (max 2 mg). Vasopressin analogue — splanchnic vasoconstriction reduces portal pressure. Use for 2–5 days for variceal bleeding or hepatorenal syndrome. Risk: hypertension, ischaemia (peripheral, cardiac, bowel). Indian brands: Glypressin (Ferring), Terlivaz.",
  },

  // ══════════════════════════════════════════════════════════════════
  // FLUIDS / GLUCOSE
  // ══════════════════════════════════════════════════════════════════
  {
    id: "dextrose",
    name: "Dextrose 10%",
    indication: "Hypoglycaemia",
    category: "fluid",
    dosePerKg: 2,
    unit: "mL",
    route: "IV (D10W)",
    notes: "2 mL/kg = 0.2 g/kg glucose.",
  },
  {
    id: "dextrose-25",
    name: "Dextrose 25%",
    indication: "Hypoglycaemia (child >1 mo)",
    category: "fluid",
    dosePerKg: 2,
    unit: "mL",
    route: "IV (D25W)",
    notes: "2 mL/kg = 0.5 g/kg glucose. Via large vein.",
  },
  {
    id: "hypertonic-saline",
    name: "Hypertonic saline 3%",
    indication: "Raised ICP / hyponatremia",
    category: "fluid",
    dosePerKg: 4,
    unit: "mL",
    route: "IV bolus (3% NaCl)",
    notes: "4 mL/kg bolus over 10–20 min.",
  },
  {
    id: "mannitol",
    name: "Mannitol 20%",
    indication: "Raised ICP",
    category: "fluid",
    dosePerKg: 0.5,
    unit: "g",
    max: 50,
    route: "IV (20% = × 5 mL/kg)",
    notes: "Give over 20 min. Monitor osmolality. Foley catheter essential.",
  },
  {
    id: "insulin-dka",
    name: "Insulin (DKA)",
    indication: "Diabetic ketoacidosis",
    category: "endocrine",
    fixedDose: "0.05–0.1 units/kg/hr",
    unit: "U/kg/hr",
    route: "IV infusion",
    notes: "Start ≥1 hr after fluids. No bolus in DKA.",
  },
  {
    id: "furosemide-iv",
    name: "Furosemide (Lasix) IV",
    indication: "Fluid overload / pulmonary oedema / hyperK",
    category: "renal",
    dosePerKg: 1,
    unit: "mg",
    max: 40,
    route: "IV",
    notes: "0.5–1 mg/kg IV (max 40 mg). Monitor K⁺ and volume. IV twice as potent as oral.",
  },

  // ══════════════════════════════════════════════════════════════════
  // IV ANTIBIOTICS (parenteral only — oral versions in oralFormulations.js)
  // ══════════════════════════════════════════════════════════════════
  {
    id: "ampicillin-iv",
    name: "Ampicillin (IV/IM)",
    indication: "Neonatal sepsis / meningitis (with aminoglycoside) / Group B Strep / Listeria",
    category: "antibiotic",
    dosePerKg: 50,
    unit: "mg",
    max: 2000,
    route: "IV / IM",
    notes: "Sepsis: 50 mg/kg q6h (neonates q8–12h). Meningitis: 100 mg/kg q6h (max 2 g/dose). Neonates ≤7 days: 50 mg/kg q12h; 8–28 days: q8h. Standard partner with gentamicin for neonatal sepsis (WHO ETAT). Indian brands: Ampicillin (Cipla, Sun, Aristo), Ampilin.",
  },
  {
    id: "cloxacillin-iv",
    name: "Cloxacillin (IV)",
    indication: "MSSA infection — skin/soft tissue / osteomyelitis / septic arthritis / endocarditis",
    category: "antibiotic",
    dosePerKg: 25,
    unit: "mg",
    max: 2000,          // ← CORRECTED: was 1000 mg. Harriet Lane/Piyush Gupta: severe infections (osteomyelitis, endocarditis) up to 50 mg/kg q4–6h, max 2 g/dose. 1000 mg cap was too restrictive.
    route: "IV over 30 min",
    notes: "Standard: 25–50 mg/kg/dose q6h (max 2 g/dose). Severe (osteomyelitis/endocarditis): 50 mg/kg q4–6h. Antistaphylococcal penicillin — drug of choice for MSSA. Indian brands: Clox (Sun), Bioclox (Cipla), Kloxerat.",
  },
  {
    id: "linezolid-iv",
    name: "Linezolid (IV/PO)",
    indication: "MRSA / VRE / MDR Gram-positive — pneumonia, skin/soft tissue, bacteraemia",
    category: "antibiotic",
    dosePerKg: 10,
    unit: "mg",
    max: 600,
    route: "IV over 30–120 min / PO",
    notes: "10 mg/kg IV/PO q8h (<12 yr); 600 mg q12h (≥12 yr or >40 kg). 100% bioavailability — IV→PO switch early. 2-week max (thrombocytopaenia, peripheral neuropathy, serotonin syndrome). Avoid tyramine-rich foods. Indian brands: Linzolid (Sun), Lizolid (Zydus), Zyvox (Pfizer).",
  },
  {
    id: "fluconazole-iv",
    name: "Fluconazole (IV/PO)",
    indication: "Candidiasis (invasive / mucocutaneous) / cryptococcal meningitis / Candida prophylaxis",
    category: "antifungal", // ← CORRECTED: was "antibiotic",
    dosePerKg: 12,
    unit: "mg",
    max: 800,
    route: "IV OD / PO OD",
    notes: "Invasive candidiasis: 12 mg/kg loading dose day 1, then 6–12 mg/kg/day OD (max 800 mg). Prophylaxis (immunocompromised): 3–6 mg/kg OD. 100% oral bioavailability — switch to PO early. Not for C. krusei or C. glabrata (resistant). Indian brands: Forcan (Cipla), Fluka (Sun), Zocon (FDC).",
  },
  {
    id: "voriconazole-iv",
    name: "Voriconazole (IV/PO)",
    indication: "Invasive aspergillosis / Candida (fluconazole-resistant) / mould infections",
    category: "antifungal", // ← CORRECTED: was "antibiotic",
    dosePerKg: 9,
    unit: "mg",
    max: 350,
    route: "IV BD (day 1: 9 mg/kg; then 8 mg/kg BD)",
    notes: "Load: 9 mg/kg IV q12h × 2 doses; maintenance: 8 mg/kg IV q12h (max 350 mg/dose). PO: 9 mg/kg BD (max 350 mg). Monitor LFTs, visual disturbance, photosensitivity. Requires TDM (target trough 1–5.5 mcg/mL). Indian brands: Vfend (Pfizer), Voritek (Sun), Voritrust (Cipla).",
  },
  {
    id: "micafungin-iv",
    name: "Micafungin (IV)",
    indication: "Invasive candidiasis / oesophageal candidiasis / Candida prophylaxis (HSCT)",
    category: "antifungal",  // ← CORRECTED: was "antibiotic",
    dosePerKg: 2,
    unit: "mg",
    max: 100,
    route: "IV over 60 min OD",
    notes: "Candidaemia: 2 mg/kg/day OD (max 100 mg). Prophylaxis: 1 mg/kg OD. Neonates: 10 mg/kg OD. Echinocandin — excellent Candida coverage. Safe in hepatic/renal impairment. No significant drug interactions. Indian brands: Mycamine (Astellas), Micatec (Sun).",
  },
  {
    id: "ceftriaxone",
    name: "Ceftriaxone",
    indication: "Sepsis / meningitis / enteric fever",
    category: "antibiotic",
    dosePerKg: 50,      // ← CORRECTED: was 100 mg/kg — this is the MENINGITIS dose. Standard sepsis = 50 mg/kg OD. Default calculator should use 50 mg/kg; see notes for meningitis.
    unit: "mg",
    max: 2000,          // max for 50 mg/kg OD standard; meningitis max = 4 g (see notes)
    route: "IV / IM",
    notes: "Standard sepsis/infections: 50 mg/kg OD (max 2 g). Meningitis: 100 mg/kg OD (max 4 g — increase max manually). Enteric fever: 75–100 mg/kg OD × 7–14 days. IM: reconstitute with 1% lignocaine. Avoid in neonates <28 days (bilirubin displacement — use cefotaxime). Harriet Lane: 50–75 mg/kg/day for most indications.",
  },
  {
    id: "gentamicin",
    name: "Gentamicin",
    indication: "Gram-negative sepsis / neonatal sepsis (with ampicillin)",
    category: "antibiotic",
    dosePerKg: 7.5,
    unit: "mg",
    max: 400,
    route: "IV over 30 min",
    notes: "Children >1 mo: 7.5 mg/kg OD. Neonates: 4–5 mg/kg q24–48h (GA-dependent). Monitor levels: trough <1 mcg/mL, peak 5–10 mcg/mL. Avoid concurrent furosemide.",
  },
  {
    id: "amikacin-iv",
    name: "Amikacin (IV)",
    indication: "MDR Gram-negative / gentamicin-resistant / neonatal sepsis",
    category: "antibiotic",
    dosePerKg: 15,
    unit: "mg",
    max: 1500,
    route: "IV over 30 min",
    notes: "15–20 mg/kg OD. Neonates: 15 mg/kg q24–48h (GA-based). Monitor levels: trough <5 mcg/mL, peak 20–30 mcg/mL. Indian brands: Amikin, Mikacin.",
  },
  {
    id: "vancomycin-iv",
    name: "Vancomycin (IV)",
    indication: "MRSA / resistant Gram-positive / meningitis / endocarditis",
    category: "antibiotic",
    dosePerKg: 15,
    unit: "mg",
    max: 750,
    route: "IV over 60 min",
    notes: "15 mg/kg q6–8h. Target trough 15–20 mcg/mL (severe). Red man syndrome if infused fast — slow rate. Indian brands: Vancorid, Vancocin.",
  },
  {
    id: "meropenem-iv",
    name: "Meropenem (IV)",
    indication: "Severe sepsis / meningitis / MDR Gram-negative",
    category: "antibiotic",
    dosePerKg: 20,
    unit: "mg",
    max: 1000,
    route: "IV over 30 min",
    notes: "Sepsis: 20 mg/kg q8h. Meningitis: 40 mg/kg q8h (max 2 g). Extended infusion (3–4 hr) for MDR. Indian brands: Meronem, Meromer.",
  },
  {
    id: "piptaz-iv",
    name: "Piperacillin-Tazobactam (Pip-Taz)",
    indication: "Nosocomial sepsis / Pseudomonas / febrile neutropenia",
    category: "antibiotic",
    dosePerKg: 100,
    unit: "mg",
    max: 4000,
    route: "IV over 30 min",
    notes: "100 mg/kg q8h (piperacillin component). Severe: q6h. Extended infusion (4 hr) for MDR. Indian brands: Tazocin, Piptaz.",
  },
  {
    id: "cefoperazone-sulbactam",
    name: "Cefoperazone-Sulbactam",
    indication: "MDR Gram-negative / Acinetobacter / biliary sepsis",
    category: "antibiotic",
    dosePerKg: 40,
    unit: "mg",
    max: 2000,
    route: "IV over 30 min",
    notes: "40–80 mg/kg q12h. Severe MDR: q8–6h. Monitor PT/INR (vitamin K antagonism). Indian brands: Magnamycin, Sulperazone, Cefobid-S.",
  },
  {
    id: "teicoplanin-iv",
    name: "Teicoplanin (IV/IM)",
    indication: "MRSA / resistant Gram-positive (preferred in Indian ICUs)",
    category: "antibiotic",
    dosePerKg: 10,
    unit: "mg",
    max: 400,
    route: "IV / IM",
    notes: "Loading: 10 mg/kg q12h × 3 doses, then 6–10 mg/kg OD. OD dosing, less nephrotoxic than vancomycin, IM possible. Indian brands: Targocid, Teicoblast.",
  },
  {
    id: "colistin-iv",
    name: "Colistin — LAST RESORT",
    indication: "XDR/PDR Acinetobacter / Pseudomonas / Klebsiella",
    category: "antibiotic",
    dosePerKg: 2.5,
    unit: "mg",
    max: 150,
    route: "IV over 60 min",
    notes: "Loading: 5 mg/kg CBA IV once. Maintenance: 2.5 mg/kg q12h. LAST RESORT — XDR/PDR only. Nephrotoxic (30–50%). Monitor creatinine daily. Combine with meropenem for synergy.",
  },
  {
    id: "cefotaxime-iv",
    name: "Cefotaxime (IV/IM)",
    indication: "Sepsis / meningitis / neonatal infection",
    category: "antibiotic",
    dosePerKg: 50,
    unit: "mg",
    max: 2000,
    route: "IV / IM",
    notes: "Sepsis: 50 mg/kg q8h. Meningitis: 75 mg/kg q6h (max 3 g/dose). Neonates: 50 mg/kg q12h (≤7d), q8h (>7d). Preferred over ceftriaxone in neonates. Indian brands: Claforan, Taxim.",
  },
  {
    id: "amphotericin-b-iv",
    name: "Amphotericin B (IV)",
    indication: "Invasive fungal / visceral leishmaniasis (kala-azar)",
    category: "antifungal",   // ← CORRECTED: was "antibiotic",
    dosePerKg: 0.5,
    unit: "mg",
    max: 50,
    route: "IV over 4–6 hr",
    notes: "Conventional: 0.5–1 mg/kg/day. Test dose 0.1 mg/kg first. Liposomal (AmBisome): 3–5 mg/kg/day. Kala-azar: 3 mg/kg liposomal days 1–5 and 14. Premedicate. Monitor K⁺, Mg²⁺, creatinine daily.",
  },
  {
    id: "artesunate-iv",
    name: "Artesunate (IV/IM)",
    indication: "Severe/complicated P. falciparum malaria",
    category: "antimalarial",  // ← CORRECTED: was "antibiotic",
    dosePerKg: 2.4,
    unit: "mg",
    max: 240,
    route: "IV / IM",
    notes: "2.4 mg/kg at 0, 12, 24 hr then OD (min 3 days). Reconstitute with 1 mL 5% NaHCO₃ then dilute in 5% dextrose. Monitor for delayed haemolysis (up to 4 weeks). Indian brands: Falcigo, Larinate.",
  },
  {
    id: "quinine-iv",
    name: "Quinine (IV)",
    indication: "Severe P. falciparum malaria (artesunate unavailable) / chloroquine-resistant malaria",
    category: "antimalarial", // ← CORRECTED: was "antibiotic",
    dosePerKg: 20,
    unit: "mg",
    max: 1400,
    route: "IV infusion over 4 hr",
    notes: "Loading: 20 mg/kg (max 1.4 g) in 10 mL/kg NS over 4 hr. Maintenance: 10 mg/kg q8h over 2–4 hr. Switch to oral when tolerated. Monitor ECG (QT prolongation), glucose (hypoglycaemia), BP. Not to be given as IV push. Indian brands: Quinine dihydrochloride (Themis, Cipla).",
  },
  {
    id: "chloroquine-iv",
    name: "Chloroquine (IV/IM)",
    indication: "P. vivax / P. malariae / sensitive P. falciparum malaria (IV when oral not possible)",
    category: "antimalarial",  // ← CORRECTED: was "antibiotic",
    dosePerKg: 10,
    unit: "mg",
    max: 600,
    route: "IV slow infusion / IM",
    notes: "IV: 10 mg base/kg over 8 hr, then 5 mg base/kg q12h (max 600 mg base/dose). IM: 3.5 mg base/kg q6h (max 25 mg base/kg/day). IV route rarely needed — use oral as soon as possible. Cardiotoxic if rapid IV — monitor ECG. Indian brands: Lariago (IPCA), Malirid (Elder).",
  },
  {
    id: "acyclovir-iv",
    name: "Acyclovir (IV)",
    indication: "HSV encephalitis / neonatal HSV / severe varicella",
    category: "antiviral",  // ← CORRECTED: was "antibiotic"
    dosePerKg: 10,          // ← CORRECTED: was 20 mg/kg — that is the neonatal/HSV encephalitis dose. Standard severe varicella = 10 mg/kg q8h; HSV encephalitis in children = 10–15 mg/kg q8h (Harriet Lane); 20 mg/kg q8h = neonatal specific. Use 10 as default; see notes for neonates.
    unit: "mg",
    max: 800,               // 10 mg/kg × 80 kg cap; Harriet Lane paed max per dose varies — keep conservative
    route: "IV over 60 min",
    notes: "Standard children: 10 mg/kg q8h IV over 60 min. HSV encephalitis (immunocompromised/severe): 15 mg/kg q8h. NEONATAL HSV: 20 mg/kg q8h × 14–21 days (increase dosePerKg manually). Severe varicella: 10 mg/kg q8h × 5–7 days. Adequate hydration essential (nephrotoxic — ensure urine output). Indian brands: Zovirax IV, Acivir IV.",
  },
  {
    id: "ganciclovir-iv",
    name: "Ganciclovir (IV)",
    indication: "CMV disease — retinitis / pneumonitis / congenital CMV",
    category: "antiviral", // ← CORRECTED: was "antibiotic",
    dosePerKg: 5,
    unit: "mg",
    max: 300,
    route: "IV over 60 min",
    notes: "Induction: 5 mg/kg IV q12h × 14–21 days. Maintenance: 5 mg/kg OD × 7 days/week. Congenital CMV: 6 mg/kg q12h × 6 weeks. Bone marrow suppression (neutropaenia) — monitor FBC weekly. Teratogenic. Switch to oral valganciclovir when possible. Indian brands: Cymevene (Roche), Virgan.",
  },
  {
    id: "oseltamivir",
    name: "Oseltamivir (Oral/NG)",
    indication: "Influenza A/B treatment and prophylaxis",
    category: "antiviral",  // ← CORRECTED: was "antibiotic",
    fixedDose: "<1 yr: 3 mg/kg BD · 1–12 yr: weight-based BD (see notes) · >40 kg: 75 mg BD × 5 days",
    unit: "mg BD PO",
    route: "PO / NG",
    notes: "1–12 yr: ≤15 kg: 30 mg BD; >15–23 kg: 45 mg BD; >23–40 kg: 60 mg BD; >40 kg: 75 mg BD × 5 days. Start within 48 hr of symptoms. ICU/severe: 150 mg BD considered. Prophylaxis: half treatment dose OD × 10 days. Indian brands: Tamiflu (Roche), Osiflu (Cipla), Fluvir (Glenmark).",
  },
  {
    id: "ribavirin-iv",
    name: "Ribavirin (IV/inhaled/PO)",
    indication: "RSV severe disease (HSCT/immunocompromised) / Lassa fever / CCHF",
    category: "antiviral",  // ← CORRECTED: was "antibiotic",
    dosePerKg: 33,
    unit: "mg",
    max: 2000,
    route: "IV (Lassa/CCHF) / Inhaled (RSV)",
    notes: "Haemorrhagic fever/Lassa: 33 mg/kg IV loading, then 16 mg/kg q6h × 4 days, then 8 mg/kg q8h × 6 days. RSV inhaled: SPAG-2 device, 20 mg/mL × 12–18 hr/day × 3–7 days. Teratogenic — staff precautions for inhaled therapy. Limited India availability. Consult ID specialist.",
  },

  // ══════════════════════════════════════════════════════════════════
  // NEONATAL SPECIFIC
  // ══════════════════════════════════════════════════════════════════
  {
    id: "adrenaline-neonatal",
    name: "Adrenaline (NEONATAL NRP)",
    indication: "NRP — HR <60 despite 30 s effective PPV",
    category: "resuscitation",
    dosePerKg: 0.01,    // ← CORRECTED: NRP 2020 IV dose is 0.01–0.03 mg/kg; standard first dose = 0.01 mg/kg IV. Was 0.02 mg/kg (midpoint — overstates initial IV dose).
    unit: "mg",
    max: 0.03,
    route: "IV/UVC (preferred) or ETT",
    notes: "NRP 2020: IV/IO preferred: 0.01–0.03 mg/kg (1:10,000). Use 0.01 mg/kg as first IV dose. ETT (if no IV access): 0.05–0.1 mg/kg (1:10,000). Repeat q3–5 min. Flush with 0.5–1 mL NS after IV dose.",
  },
  {
    id: "volume-neonate",
    name: "Volume expander (NEONATAL)",
    indication: "NRP — hypovolaemia / shock",
    category: "fluid",
    dosePerKg: 10,
    unit: "mL",
    route: "IV/UVC over 5–10 min",
    notes: "10 mL/kg NS or O-neg blood. May repeat.",
  },
  {
    id: "prostaglandin-e1",
    name: "Prostaglandin E1 (Alprostadil)",
    indication: "Duct-dependent congenital heart disease",
    category: "resuscitation",
    fixedDose: "0.05–0.1 mcg/kg/min",
    unit: "mcg/kg/min",
    route: "IV infusion",
    notes: "Keep ductus arteriosus patent. Start low to avoid apnoea. Intubate if ≥0.05 mcg/kg/min.",
  },
  {
    id: "caffeine-citrate",
    name: "Caffeine citrate",
    indication: "Apnoea of prematurity",
    category: "neonate",
    dosePerKg: 20,
    unit: "mg",
    max: 40,
    route: "IV / PO",
    notes: "Load 20 mg/kg IV/PO, maintenance 5–10 mg/kg/day. As citrate salt (note: caffeine base = half citrate dose).",
  },
  {
    id: "surfactant",
    name: "Surfactant (Poractant/Beractant)",
    indication: "Neonatal RDS",
    category: "airway",
    fixedDose: "Poractant 200 mg/kg ET · Beractant 100 mg/kg ET",
    unit: "mg/kg ET",
    route: "Endotracheal",
    notes: "Via ETT. Poractant (Curosurf) or Beractant (Survanta). May repeat q6–12 h.",
  },
  {
    id: "vitamin-k-neonatal",
    name: "Vitamin K (Phytomenadione)",
    indication: "Neonatal prophylaxis (VKDB)",
    category: "neonate",
    fixedDose: "1 mg IM (0.5 mg if <1.5 kg)",
    unit: "mg IM",
    route: "IM at birth",
    notes: "Routine neonatal prophylaxis within 6 hr of birth.",
  },
  {
    id: "naloxone-neonate",
    name: "Naloxone (NEONATAL)",
    indication: "Opioid-induced respiratory depression (NOT routine in NRP)",
    category: "resuscitation",
    dosePerKg: 0.1,
    unit: "mg",
    max: 2,
    route: "IV / IM",
    notes: "Not first-line in NRP. Focus on ventilation first. Avoid if mother on chronic opioids.",
  },

  // ══════════════════════════════════════════════════════════════════
  // RESPIRATORY
  // ══════════════════════════════════════════════════════════════════
   {
    id: "racemic-epi",
    name: "Racemic epinephrine",
    indication: "Croup (stridor at rest)",
    category: "resuscitation",
    fixedDose: "0.05 mL/kg of 2.25% neb (max 0.5 mL)",
    unit: "mL neb",
    route: "Nebulised",
    notes: "Observe ≥2–4 hr post-dose for rebound. Adrenaline 1:1000 (1 mg/mL) 0.5 mL/kg (max 5 mL) is acceptable alternative.",
  },
     {
    id: "dexamethasone-croup",
    name: "Dexamethasone (croup)",
    indication: "Croup",
    category: "resuscitation",
    dosePerKg: 0.6,
    unit: "mg",
    max: 10,
    route: "PO / IM / IV",
    notes: "Single dose. PO as effective as IM/IV (Piyush Gupta). Injection solution can be given orally.",
  },
{
  id: "salbutamol-iv",
  name: "Salbutamol (IV infusion)",
  indication: "Severe / critical asthma unresponsive to nebulisation",
  category: "resuscitation",
  fixedDose: "Loading: 15 mcg/kg over 10 min, then 1–5 mcg/kg/min infusion",
  unit: "mcg/kg/min",
  route: "IV infusion",
  notes: "Load: 15 mcg/kg in 25 mL NS over 10 min. Maintenance: 1–5 mcg/kg/min. Monitor HR, K⁺ (hypokalaemia), glucose. Tachycardia expected. Indian brands: Ventorlin IV (GSK), Asthalin IV (Cipla).",
},

// 2. Aminophylline IV — still widely used Indian EDs
{
  id: "aminophylline-iv",
  name: "Aminophylline (IV)",
  indication: "Severe asthma / apnoea of prematurity (loading dose)",
  category: "resuscitation",
  dosePerKg: 5,
  unit: "mg",
  max: 500,
  route: "IV over 20–30 min",
  notes: "Load: 5 mg/kg IV over 20–30 min (max 500 mg). Omit load if already on oral theophylline. Maintenance: 0.5–1 mg/kg/hr. Monitor ECG, HR (arrhythmia risk). Narrow therapeutic index — toxic at >20 mcg/mL. Indian brands: Deriphyllin (Abbott), Aminophylline (Neon).",
},

// 3. Tramadol IV/IM — most common IV analgesic in Indian EDs
{
  id: "tramadol-iv",
  name: "Tramadol (IV/IM)",
  indication: "Moderate-severe pain (>12 yr / >50 kg)",
  category: "analgesia",
  dosePerKg: 1,
  unit: "mg",
  max: 100,
  route: "IV slow over 15 min / IM",
  notes: "1–2 mg/kg IV slow over 15 min (max 100 mg/dose). Avoid <12 yr (FDA warning — respiratory depression risk). IM: 1–2 mg/kg. Nausea common — premedicate with ondansetron. Lowers seizure threshold. Indian brands: Tramazac (Zydus), Ultracet (J&J — with paracetamol), Tramadol (Sun).",
},

// 4. Diclofenac IV/IM — most used IM analgesic in India
{
  id: "diclofenac-im",
  name: "Diclofenac (IV/IM)",
  indication: "Moderate-severe pain / renal colic / post-op pain (>12 yr)",
  category: "analgesia",
  dosePerKg: 1,
  unit: "mg",
  max: 75,
  route: "IM deep / IV slow infusion",
  notes: "1 mg/kg IM (max 75 mg). IV: dilute in 100 mL NS, infuse over 30–60 min. Do NOT give IV bolus. Max 150 mg/day. Avoid <1 yr, renal impairment, GI bleed, dengue. Give IM into upper outer quadrant gluteal, alternating sides. Indian brands: Voveran (Novartis), Diclofenac (Sun, Cipla), Reactin-D.",
},

// 5. Metronidazole IV — daily antibiotic, only oral exists in drugs.js
{
  id: "metronidazole-iv",
  name: "Metronidazole (IV)",
  indication: "Anaerobic infections / intra-abdominal sepsis / brain abscess / C. difficile",
  category: "antibiotic",
  dosePerKg: 7.5,
  unit: "mg",
  max: 500,
  route: "IV over 30–60 min",
  notes: "7.5 mg/kg IV q8h (max 500 mg/dose). Infuse over 30–60 min. Peripheral IV acceptable. Anaerobic cover with ceftriaxone/meropenem for abdominal sepsis. Brain abscess: 10 mg/kg q8h. Switch to oral as soon as tolerated (100% bioavailability). Indian brands: Flagyl IV (Abbott), Metrogyl IV (JB Chemicals).",
},

// 6. Clindamycin IV — necrotising fasciitis, anaerobic, toxic shock
{
  id: "clindamycin-iv",
  name: "Clindamycin (IV)",
  indication: "Necrotising fasciitis / toxin suppression (GAS/MRSA) / anaerobic / osteomyelitis",
  category: "antibiotic",
  dosePerKg: 10,
  unit: "mg",
  max: 600,
  route: "IV over 30 min",
  notes: "10 mg/kg IV q6–8h (max 600 mg/dose). Must dilute (≤18 mg/mL) — infuse over 30–60 min; rapid IV causes cardiac arrest. Toxin-suppressing agent in NF/TSS — combine with beta-lactam. C. difficile risk — monitor stools. Indian brands: Dalacin C (Pfizer), Clindamycin (Sun).",
},

// 7. Azithromycin IV — severe CAP/atypical pneumonia
{
  id: "azithromycin-iv",
  name: "Azithromycin (IV)",
  indication: "Severe CAP / atypical pneumonia when oral not tolerated",
  category: "antibiotic",
  dosePerKg: 10,
  unit: "mg",
  max: 500,
  route: "IV over 60 min",
  notes: "10 mg/kg IV OD (max 500 mg). Infuse over 60 min (NEVER bolus — thrombophlebitis, QT). Switch to oral azithromycin as soon as tolerated (100% bioavailability). QT monitoring. Indian brands: Azithral IV (Alembic), Azee IV (Cipla).",
},

// 8. Doxycycline IV — scrub typhus, rickettsial
{
  id: "doxycycline-iv",
  name: "Doxycycline (IV)",
  indication: "Scrub typhus / rickettsial disease / severe atypical pneumonia (>8 yr)",
  category: "antibiotic",
  dosePerKg: 2.2,
  unit: "mg",
  max: 100,
  route: "IV over 1–4 hr",
  notes: "2.2 mg/kg IV q12h (max 100 mg/dose). Only >8 yr. Dilute in NS/D5W, infuse over 1–4 hr (phlebitis risk — use largest vein). Switch to oral as soon as tolerated. Scrub typhus: 2.2 mg/kg IV OD or BD × 7 days. Indian brands: Doxycycline IV (limited India availability — use oral where possible).",
},

// 9. Thiamine IV — before glucose in encephalopathy, Wernicke's
{
  id: "thiamine-iv",
  name: "Thiamine (Vitamin B1) IV/IM",
  indication: "Wernicke's encephalopathy / alcoholic / malnutrition / give BEFORE glucose in AMS",
  category: "resuscitation",
  dosePerKg: 1,
  unit: "mg",
  max: 100,
  route: "IV over 30 min / IM",
  notes: "1–2 mg/kg IV (max 100 mg) over 30 min BEFORE glucose in malnourished/alcoholic encephalopathy. Anaphylaxis risk with rapid IV — give slowly. Wernicke's: 100 mg IV TDS × 3 days. Indian brands: Benfotiamine (oral), Neurobion IV, Thiamine HCl injection (Troikaa, Neon).",
},

// 10. Activated Charcoal — completely missing, first-line GI decontamination
{
  id: "activated-charcoal",
  name: "Activated Charcoal",
  indication: "Acute poisoning GI decontamination — within 1–2 hr of ingestion",
  category: "toxicology",
  dosePerKg: 1,
  unit: "g",
  max: 50,
  route: "PO / NG",
  notes: "1 g/kg (max 50 g) in water as slurry. Give within 1–2 hr of ingestion. NOT for: acids, alkalis, hydrocarbons, iron, lithium, alcohols (not adsorbed), comatose patient without protected airway. Multiple-dose: 0.5 g/kg q4h for sustained-release drugs (carbamazepine, theophylline). Indian brands: Medichar (Torrent), Novalcarb (Novalab).",
},

// 11. Hydroxocobalamin IV — cyanide poisoning / smoke inhalation
{
  id: "hydroxocobalamin-iv",
  name: "Hydroxocobalamin (IV)",
  indication: "Cyanide poisoning / smoke inhalation with suspected cyanide",
  category: "toxicology",
  fixedDose: "5 g IV over 15 min (adult). Child: 70 mg/kg IV over 15 min",
  unit: "mg/kg",
  route: "IV over 15 min",
  notes: "70 mg/kg IV (max 5 g) over 15 min. Repeat up to 3 doses. Turns urine/skin/mucous membranes red — harmless, warn staff. Incompatible with sodium thiosulfate in same line. Indian brands: Cyanokit (Meridian) — very limited India availability; sodium thiosulfate is practical alternative.",
},

// 12. Sodium Thiosulfate IV — cyanide poisoning (more available in India)
{
  id: "sodium-thiosulfate-iv",
  name: "Sodium Thiosulfate (IV)",
  indication: "Cyanide poisoning (with sodium nitrite) / cisplatin nephrotoxicity prevention",
  category: "toxicology",
  dosePerKg: 400,
  unit: "mg",
  max: 12500,
  route: "IV over 10 min",
  notes: "400 mg/kg (= 1.65 mL/kg of 25% solution) IV over 10 min (max 12.5 g). Use after sodium nitrite 3% (0.33 mL/kg IV). More available than hydroxocobalamin in India. Indian brands: Sodium Thiosulfate injection (Neon, IDPL).",
},

// 13. Potassium Chloride IV — hypokalaemia replacement
{
  id: "potassium-chloride-iv",
  name: "Potassium Chloride (IV infusion)",
  indication: "Hypokalaemia correction",
  category: "fluid",
  fixedDose: "0.3–0.5 mEq/kg/hr infusion (max 1 mEq/kg/hr). NEVER bolus.",
  unit: "mEq/hr",
  route: "IV infusion (NEVER bolus)",
  notes: "Max concentration: 40 mEq/L peripheral, 80 mEq/L central. Max rate: 0.5 mEq/kg/hr (ECG monitoring if >0.3 mEq/kg/hr). NEVER give undiluted or as IV push — fatal cardiac arrest. Correct K⁺ before correcting Mg²⁺ if both low. Indian brands: KCl 15% injection (Baxter, Fresenius) — MUST dilute.",
},

// 14. Isoprenaline IV — complete heart block / refractory bradycardia bridge
{
  id: "isoprenaline-iv",
  name: "Isoprenaline (Isoproterenol) Infusion",
  indication: "Complete heart block / refractory bradycardia / bridge to pacing",
  category: "resuscitation",
  fixedDose: "0.05–2 mcg/kg/min IV infusion — titrate to HR",
  unit: "mcg/kg/min",
  route: "IV infusion (central preferred)",
  notes: "Start 0.05 mcg/kg/min, titrate to target HR. β1+β2 agonist — chronotrope + inotrope. Causes vasodilation (reflex in shock — use with caution). Tachyarrhythmia risk. Bridge to transcutaneous/transvenous pacing in CHB. Indian brands: Isoprenaline HCl (Neon, IDPL — Isuprel). Limited availability.",
},

// 15. Proxymetacaine eye drops — corneal procedures
{
  id: "proxymetacaine-eye",
  name: "Proxymetacaine 0.5% Eye Drops",
  indication: "Topical ocular anaesthesia — corneal FB / abrasion / tonometry / slit lamp exam",
  category: "analgesia",
  fixedDose: "1–2 drops into affected eye, repeat in 5–10 min if needed",
  unit: "drops",
  route: "Topical ocular",
  notes: "Onset 30 s, duration 15–20 min. Do NOT give bottle to patient (risk of inadvertent repeated use causing corneal damage). Do NOT patch after topical anaesthesia. Indian brands: Proparacaine 0.5% (Sun Ophthalmic), Alcaine (Alcon), Proxymethacaine (Cipla).",
},

// 16. Pyridoxine IV — INH-induced seizures / neonatal pyridoxine-dependent epilepsy
{
  id: "pyridoxine-iv",
  name: "Pyridoxine (Vitamin B6) IV",
  indication: "INH toxicity seizures / pyridoxine-dependent epilepsy / neonatal seizures",
  category: "anticonvulsant",
  dosePerKg: 70,
  unit: "mg",
  max: 5000,
  route: "IV over 30–60 min",
  notes: "INH overdose: gram-for-gram with INH ingested (if unknown: 70 mg/kg IV, max 5 g). Pyridoxine-dependent epilepsy: 100 mg IV trial (neonatal). Prophylaxis with INH therapy: 5–10 mg/day oral. Indian brands: Pyridoxine HCl injection (Neon, Troikaa), Benadon (oral).",
},

// 17. Lacosamide IV — increasingly used 2nd line status epilepticus
{
  id: "lacosamide-iv",
  name: "Lacosamide (IV)",
  indication: "Status epilepticus 2nd line (after benzodiazepines + LEV/phenytoin) / focal epilepsy",
  category: "anticonvulsant",
  dosePerKg: 5,
  unit: "mg",
  max: 300,
  route: "IV over 15–60 min",
  notes: "5 mg/kg IV over 15–60 min (max 300 mg). 2nd/3rd line for SE in India — increasing use. Minimal haemodynamic effects. PR prolongation — ECG monitoring. 100% oral bioavailability — switch to PO. Indian brands: Lacosam (Sun), Lacosamide (Intas, Cipla). Vimpat (UCB — reference brand).",
},
];
// ─────────────────────────────────────────────────────────────────────────────
// DRUG CATEGORIES
// ─────────────────────────────────────────────────────────────────────────────
export const DRUG_CATEGORIES = [
  { id: "all",              label: "All",                 matches: null },
  { id: "resuscitation",   label: "Resuscitation",        matches: ["resuscitation"] },
  { id: "rsi-sedation",    label: "RSI / Sedation",       matches: ["sedation", "airway"] },
  { id: "anticonvulsant",  label: "Anticonvulsant",       matches: ["anticonvulsant"] },
  { id: "analgesia",       label: "Analgesia",            matches: ["analgesia"] },
  { id: "antibiotic",      label: "Antibiotic (IV)",      matches: ["antibiotic"] },
  { id: "antihypertensive",label: "Antihypertensive",     matches: ["antihypertensive"] },
  { id: "antiarrhythmic",  label: "Antiarrhythmic",       matches: ["antiarrhythmic"] },
  { id: "pulmonary-ht",    label: "Pulmonary HT",         matches: ["pulmonary-ht"] },
  { id: "endocrine",       label: "Endocrine",            matches: ["endocrine"] },
  { id: "toxicology",      label: "Toxicology",           matches: ["toxicology"] },
  { id: "haematology",     label: "Haematology",          matches: ["haematology"] },
  { id: "renal",           label: "Renal",                matches: ["renal"] },
  { id: "gi-hepatic",      label: "GI / Hepatic",         matches: ["gi-hepatic"] },
  { id: "fluid",           label: "Fluid / Electrolyte",  matches: ["fluid"] },
  { id: "neonate",         label: "Neonatal",             matches: ["neonate"] },
];

// ─────────────────────────────────────────────────────────────────────────────
// DOSE CALCULATOR
// ─────────────────────────────────────────────────────────────────────────────
export function computeDrugDose(drug, weight) {
  if (drug.fixedDose) return drug.fixedDose;
  if (drug.dosePerKg == null) return "—";
  let d = weight * drug.dosePerKg;
  if (drug.max != null) d = Math.min(d, drug.max);
  if (drug.min != null) d = Math.max(d, drug.min);
  return `${+d.toFixed(2)} ${drug.unit}`;
}
